import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
    Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
    Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Check, ChevronsUpDown } from "lucide-react";
import { toast } from "sonner";

// Local YYYY-MM-DD — avoids UTC shifting "today" to tomorrow late at night.
function localTodayISO() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const STATUSES = ["Pending", "Quoted", "Bound", "Declined"];
const LOBS = ["GL", "A&H", "GL & A&H", "Property", "Crime", "Cyber", "Other"];

export default function SubmissionFormDialog({ open, onOpenChange, submission, brokers, onSuccess }) {
    const isEdit = !!submission;
    const [form, setForm] = useState({});
    const [brokerOpen, setBrokerOpen] = useState(false);
    const [saving, setSaving] = useState(false);

    const reset = () => {
        if (submission) {
            setForm({ ...submission });
        } else {
            setForm({
                broker_id: "",
                broker_name: "",
                named_insured: "",
                effective_date: localTodayISO(),
                estimated_premium: 0,
                bound_premium: 0,
                lob: "GL",
                status: "Pending",
                notes: "",
            });
        }
    };

    // Re-init form whenever the dialog opens / target changes
    if (open && Object.keys(form).length === 0) reset();

    const submit = async (e) => {
        e.preventDefault();
        if (!form.broker_name || !form.named_insured) {
            toast.error("Broker and Named Insured are required");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                broker_id: form.broker_id || null,
                broker_name: form.broker_name,
                named_insured: form.named_insured,
                effective_date: form.effective_date || null,
                estimated_premium: parseFloat(form.estimated_premium) || 0,
                bound_premium: parseFloat(form.bound_premium) || 0,
                lob: form.lob,
                status: form.status,
                notes: form.notes,
            };
            if (isEdit) {
                await api.patch(`/submissions/${submission.id}`, payload);
                toast.success("Submission updated");
            } else {
                await api.post("/submissions", payload);
                toast.success("Submission logged");
            }
            onSuccess?.();
            onOpenChange(false);
            setForm({});
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const handleOpenChange = (o) => {
        if (!o) setForm({});
        onOpenChange(o);
    };

    const selectedBroker = brokers.find((b) => b.id === form.broker_id);

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto scrollbar-thin" data-testid="submission-form-dialog">
                <DialogHeader>
                    <DialogTitle className="text-lg font-semibold text-ph-text">
                        {isEdit ? "Edit submission" : "Log submission"}
                    </DialogTitle>
                    <DialogDescription className="text-sm text-ph-muted">
                        Track each account submitted to or bound by Players Health.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={submit} className="space-y-4 pt-2" data-testid="submission-form">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field label="Broker *" full>
                            <Popover open={brokerOpen} onOpenChange={setBrokerOpen}>
                                <PopoverTrigger asChild>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        role="combobox"
                                        className="w-full justify-between h-10 font-normal"
                                        data-testid="submission-broker-trigger"
                                    >
                                        {selectedBroker
                                            ? selectedBroker.agency_name
                                            : (form.broker_name || "Select broker…")}
                                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                                    <Command>
                                        <CommandInput placeholder="Search brokers…" />
                                        <CommandList>
                                            <CommandEmpty>No broker found.</CommandEmpty>
                                            <CommandGroup>
                                                {brokers.map((b) => (
                                                    <CommandItem
                                                        key={b.id}
                                                        value={b.agency_name}
                                                        onSelect={() => {
                                                            setForm((f) => ({ ...f, broker_id: b.id, broker_name: b.agency_name }));
                                                            setBrokerOpen(false);
                                                        }}
                                                    >
                                                        <Check className={`mr-2 h-4 w-4 ${form.broker_id === b.id ? "opacity-100" : "opacity-0"}`} />
                                                        <span>{b.agency_name}</span>
                                                    </CommandItem>
                                                ))}
                                            </CommandGroup>
                                        </CommandList>
                                    </Command>
                                </PopoverContent>
                            </Popover>
                            <p className="text-[11px] text-ph-mutedSoft mt-1">
                                Not in the broker tracker yet? Just type the name below — we'll keep it as free text.
                            </p>
                            <Input
                                placeholder="Or type a broker name"
                                value={form.broker_name || ""}
                                onChange={(e) => setForm({ ...form, broker_id: "", broker_name: e.target.value })}
                                className="h-10 mt-2"
                                data-testid="submission-broker-name-input"
                            />
                        </Field>
                        <Field label="Named insured *" full>
                            <Input
                                required
                                value={form.named_insured || ""}
                                onChange={(e) => setForm({ ...form, named_insured: e.target.value })}
                                placeholder="The actual account being insured"
                                data-testid="submission-named-insured-input"
                            />
                        </Field>
                        <Field label="Effective date">
                            <Input
                                type="date"
                                value={form.effective_date || ""}
                                onChange={(e) => setForm({ ...form, effective_date: e.target.value })}
                                data-testid="submission-effective-date-input"
                            />
                        </Field>
                        <Field label="Line of business">
                            <Select value={form.lob || "GL"} onValueChange={(v) => setForm({ ...form, lob: v })}>
                                <SelectTrigger data-testid="submission-lob-trigger"><SelectValue /></SelectTrigger>
                                <SelectContent>{LOBS.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                            </Select>
                        </Field>
                        <Field label="Estimated premium ($)">
                            <Input
                                type="number"
                                value={form.estimated_premium || 0}
                                onChange={(e) => setForm({ ...form, estimated_premium: e.target.value })}
                                data-testid="submission-est-input"
                            />
                        </Field>
                        <Field label="Bound premium ($)">
                            <Input
                                type="number"
                                value={form.bound_premium || 0}
                                onChange={(e) => setForm({ ...form, bound_premium: e.target.value })}
                                data-testid="submission-bound-input"
                            />
                        </Field>
                        <Field label="Status">
                            <Select value={form.status || "Pending"} onValueChange={(v) => setForm({ ...form, status: v })}>
                                <SelectTrigger data-testid="submission-status-trigger"><SelectValue /></SelectTrigger>
                                <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                            </Select>
                            <p className="text-[11px] text-ph-mutedSoft mt-1">
                                Auto-set to "Bound" when bound premium is greater than 0.
                            </p>
                        </Field>
                        <Field label="Notes" full>
                            <Textarea
                                rows={3}
                                value={form.notes || ""}
                                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                data-testid="submission-notes-input"
                            />
                        </Field>
                    </div>

                    <div className="flex justify-end gap-3 pt-3 border-t border-ph-borderSoft">
                        <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} className="font-medium" data-testid="submission-cancel-btn">
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={saving}
                            className="bg-ph-primary text-white font-medium hover:bg-ph-primaryHover"
                            data-testid="submission-submit-btn"
                        >
                            {saving ? "Saving…" : isEdit ? "Save changes" : "Log submission"}
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function Field({ label, children, full }) {
    return (
        <div className={full ? "md:col-span-2" : ""}>
            <label className="text-xs font-medium text-ph-text mb-1.5 block">{label}</label>
            {children}
        </div>
    );
}
